/**
 * <swiper-lazy> — Generic Web Component for lazy-loaded Swiper sliders.
 *
 * Uses IntersectionObserver to defer Swiper loading until the element
 * enters the viewport. Swiper JS/CSS is imported once and shared across
 * all instances on the page.
 *
 * ── Data-attributes (simple options) ─────────────────────────────
 *  data-speed            Transition speed in ms          (default: 1000)
 *  data-delay            Autoplay delay in ms            (default: 3000)
 *  data-loop             "true" | "false"                (default: "false")
 *  data-autoplay         "true" | "false"                (default: "false")
 *  data-pause-on-hover   "true" | "false"                (default: "false")
 *  data-root-margin      IntersectionObserver margin     (default: "200px")
 *
 * ── JSON config (Swiper options) ─────────────────────────────────
 *  Add a <script type="application/json"> inside the component for
 *  any Swiper option (slidesPerView, spaceBetween, centeredSlides,
 *  breakpoints, etc.). JSON config is spread directly into the
 *  Swiper constructor; data-attributes take priority for the keys
 *  they cover.
 *
 *  <swiper-lazy data-loop="true" data-autoplay="true">
 *      <script type="application/json">
 *      {
 *          "slidesPerView": "auto",
 *          "spaceBetween": 40,
 *          "centeredSlides": true,
 *          "breakpoints": {
 *              "480":  { "slidesPerView": 2, "slidesPerGroup": 2 },
 *              "1024": { "slidesPerView": 3, "slidesPerGroup": 3 },
 *              "1240": { "slidesPerView": 4, "slidesPerGroup": 4 }
 *          }
 *      }
 *      </script>
 *      <div class="swiper-wrapper">…</div>
 *      <button class="swiper-button-prev">…</button>
 *      <button class="swiper-button-next">…</button>
 *      <div class="swiper-pagination"></div>
 *  </swiper-lazy>
 *
 * ── DOM conventions ──────────────────────────────────────────────
 *  Navigation  → .swiper-button-prev / .swiper-button-next
 *  Pagination  → .swiper-pagination
 *  (auto-detected inside the component)
 */

import type Swiper from 'swiper';
import { setModifier } from '../animations/cursor';


// ── Singleton module cache ──────────────────────────────────────────
// Swiper core + modules are fetched once, then shared by every instance.
// CSS is imported as inline strings (?inline) and injected once into <head>.
let swiperPromise: Promise<{
    Swiper: typeof Swiper;
    Navigation: any;
    Pagination: any;
    Autoplay: any;
}> | null = null;

let cssInjected = false;

function injectCSS(css: string): void {
    const style = document.createElement('style');
    style.setAttribute('data-swiper-lazy', '');
    style.textContent = css;
    document.head.appendChild(style);
}

function loadSwiper() {
    if (!swiperPromise) {
        swiperPromise = Promise.all([
            import('swiper'),
            import('swiper/modules'),
            // @ts-ignore — ?inline returns a string, not a CSS module
            // import('swiper/css?inline'),
            // @ts-ignore
            // import('swiper/css/navigation?inline'),
            // @ts-ignore
            // import('swiper/css/pagination?inline'),
        ]).then(([core, modules, swiperCSS, navCSS, pagCSS]) => {
            if (!cssInjected) {
                cssInjected = true;
                // injectCSS(swiperCSS.default);
                // injectCSS(navCSS.default);
                // injectCSS(pagCSS.default);
            }

            return {
                Swiper: core.default,
                Navigation: modules.Navigation,
                Pagination: modules.Pagination,
                Autoplay: modules.Autoplay,
            };
        });
    }
    return swiperPromise;
}

// ── Helper ──────────────────────────────────────────────────────────
function data(el: HTMLElement, name: string, fallback: string): string {
    return el.dataset[name] ?? fallback;
}

// ── Web Component ───────────────────────────────────────────────────
class SwiperLazy extends HTMLElement {
    private swiper: Swiper | null = null;
    private observer: IntersectionObserver | null = null;
    private _hydrated = false;

    connectedCallback() {
        const rootMargin = data(this, 'rootMargin', '50px');

        this.observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    this.hydrate();
                    this.observer?.disconnect();
                    this.observer = null;
                }
            },
            { rootMargin },
        );

        this.observer.observe(this);
    }

    disconnectedCallback() {
        this.observer?.disconnect();
        this.swiper?.destroy(true, true);
        this.swiper = null;
    }

    // ── Read inline JSON config ─────────────────────────────────────
    private getJsonConfig(): Record<string, any> {
        const script = this.querySelector('script[type="application/json"]');
        if (!script?.textContent) return {};

        try {
            return JSON.parse(script.textContent);
        } catch (e) {
            console.warn('<swiper-lazy> Invalid JSON config:', e);
            return {};
        }
    }

    // ── Hydration ───────────────────────────────────────────────────
    private async hydrate(): Promise<void> {
        if (this._hydrated) return;
        this._hydrated = true;

        const { Swiper, Navigation, Pagination, Autoplay } = await loadSwiper();

        // ── JSON config (base, can be overridden by data-attributes) ─
        const jsonConfig = this.getJsonConfig();

        // ── Data-attributes (priority over JSON) ─────────────────────
        const speed          = parseInt(data(this, 'speed', '1000'), 10);
        const delay          = parseInt(data(this, 'delay', '3000'), 10);
        const loop           = data(this, 'loop', 'false') === 'true';
        const autoplay       = data(this, 'autoplay', 'false') === 'true';
        const pauseHover     = data(this, 'pauseOnHover', 'false') === 'true';

        // ── Auto-detect navigation & pagination from DOM ─────────────
        const prevEl       = this.querySelector<HTMLElement>('.swiper-button-prev');
        const nextEl       = this.querySelector<HTMLElement>('.swiper-button-next');
        const paginationEl = this.querySelector<HTMLElement>('.swiper-pagination');

        const hasNavigation = !!(prevEl && nextEl);
        const hasPagination = !!paginationEl;

        // ── Modules ──────────────────────────────────────────────────
        const modules: any[] = [];
        if (hasNavigation) modules.push(Navigation);
        if (hasPagination) modules.push(Pagination);
        if (autoplay)      modules.push(Autoplay);

        // ── Merge: JSON config (base) → data-attributes (override) ───
        const host = this as unknown as HTMLElement;

        const config: Record<string, any> = {
            // JSON config as base (slidesPerView, spaceBetween, centeredSlides, etc.)
            ...jsonConfig,

            // Data-attributes override JSON
            modules,
            speed,
            loop,

            // Grabbing class on drag
            on: {
                lock() {
                    host.classList.add('swiper-is-locked');
                },
                unlock() {
                    host.classList.remove('swiper-is-locked');
                },
                sliderMove() {
                    if (this.isLocked) return;
                    host.classList.add('grabbing');
                    setModifier('grab');
                },
                touchEnd() {
                    if (this.isLocked) return;
                    host.classList.remove('grabbing');
                    setModifier(null);
                },
                ...jsonConfig.on,
            },
        };

        // Navigation
        if (hasNavigation) {
            config.navigation = {
                prevEl,
                nextEl,
            }
        }

        // Pagination
        if (hasPagination) {
            const paginationConfig: Record<string, any> = {
                clickable: true,
                el: paginationEl,
                ...jsonConfig.pagination,
            }

            // renderBullet preset
            if (jsonConfig.pagination?.renderBullet === 'indexed') {
                paginationConfig.renderBullet = (index: number, className: string) =>
                    `<span class="${className}">${String(index + 1).padStart(2, '0')}</span>`
            }

            config.pagination = paginationConfig
        }

        // Autoplay
        if (autoplay) {
            config.autoplay = {
                delay,
                disableOnInteraction: false,
                pauseOnMouseEnter: pauseHover,
                ...jsonConfig.autoplay,
            };
        }
        // ── Init ─────────────────────────────────────────────────────
        this.swiper = new Swiper(host, config);
        this.classList.add('swiper-initialized');
    }
}

// ── Register ────────────────────────────────────────────────────────
if (!customElements.get('swiper-lazy')) {
    customElements.define('swiper-lazy', SwiperLazy);
}

// Précharge les chunks Swiper dès que la page contient un slider,
// sans attendre l'IntersectionObserver. L'initialisation reste lazy.
if (document.querySelector('swiper-lazy')) {
    loadSwiper();
}

export { SwiperLazy };
